import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import "./styles/globals.css";
import "./styles/reduced-motion.css";
import { applyViewportFix } from "./utils/viewportFix";
import { installClientLogger } from "./utils/clientLogger";
import { installBreadcrumbs } from "./utils/breadcrumbs";
import { installNetworkRecorder } from "./utils/networkRecorder";
import { installErrorReporter } from "./utils/errorReporter";
import { initKeyboardHandler } from "./utils/keyboardHandler";
import { initTauriAutoUpdater } from "./platform/tauri/tauriUpdater";
import { initDesktopShell } from "./platform/tauri/nativeShell";
import { applyWindowsShellAttribute } from "./platform/tauri/windowChrome";
import { applySolidChromeAttribute } from "./platform/navigation/solidChromeFlag";
import { applyDesktopFrameAttribute } from "./platform/navigation/desktopFrame";
import { WindowTitleBar } from "./platform/navigation/WindowTitleBar";
import { installEdgeFunctionRetry } from "./utils/edgeFunctionRetry";
import { installDemoFetchGate } from "./services/dataService/demoFetchGate";
import {
  setDocOpenTracingEnabled,
  isDocOpenTracingEnabled,
} from "./utils/docOpenTrace";

// Attach the doc-open trace toggle directly here so it's guaranteed to
// exist on `window` from the very first frame — no module-graph dependency
// on the Documents tab being mounted first.
if (typeof window !== "undefined") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).quantisDocTrace = {
    enable:  () => { setDocOpenTracingEnabled(true);  return "tracing on"; },
    disable: () => { setDocOpenTracingEnabled(false); return "tracing off"; },
    enabled: () => isDocOpenTracingEnabled(),
  };
}

// Strip generated inspector props (_fgT, _fgS, _fgB, etc.) before they reach
// native DOM elements and trigger React unknown-prop warnings.
const _origCreateElement = React.createElement;
// @ts-ignore
React.createElement = function (type: any, props: any, ...children: any[]) {
  if (typeof type === "string" && props) {
    const cleaned: Record<string, unknown> = {};
    for (const key in props) {
      if (!/^_fg[a-z]/i.test(key)) cleaned[key] = props[key];
    }
    return _origCreateElement(type, cleaned, ...children);
  }
  return _origCreateElement(type, props, ...children);
} as typeof React.createElement;

// Belt-and-braces: the native bootstrap sets data-mac-shell at documentStart,
// but apply it here too in case the attribute races with DOMContentLoaded in
// edge cases (e.g. slow injection order). No-op if already set.
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (
    window.__QUANTIS_NATIVE__ === true &&
    document.documentElement.getAttribute('data-quantis-native-platform') === 'macos' &&
    document.documentElement.getAttribute('data-mac-shell') !== 'true'
  ) {
    document.documentElement.setAttribute('data-mac-shell', 'true');
  }
}

// Both must run before React mounts
installEdgeFunctionRetry(); // retry 503 cold-start responses from the edge function
installDemoFetchGate();     // short-circuit edge-function fetches in demo mode
applyViewportFix();
installClientLogger({ maxEntries: 1000 });
installNetworkRecorder();  // rolling 30-call fetch log for bug reports
installBreadcrumbs();      // rolling 50-event user-action log for bug reports
installErrorReporter(); // captures async/event-handler errors → support_tickets table
initKeyboardHandler();
void initTauriAutoUpdater();
void initDesktopShell(); // desktop menu events + quantis:// deep links
applySolidChromeAttribute(); // flag <html> for the solid desktop chrome
applyDesktopFrameAttribute(); // flag <html> for the desktop frame (width-driven, any pointer)
applyWindowsShellAttribute(); // flag <html> for the borderless Windows frame

declare global {
    interface Window {
        onBiometricSuccess?: () => void;
        onBiometricFail?: (reason?: string) => void;
    }
}

// Expose global callbacks for native biometric bridge. NativeBridge.ts's
// constructor assigns these first (module imports evaluate before this
// module's body runs) to resolve NativeBridge.biometric()'s promise — chain
// its previous value rather than overwriting it, or that resolver is dead.
const previousOnBiometricSuccess = window.onBiometricSuccess;
window.onBiometricSuccess = () => {
    previousOnBiometricSuccess?.();
    window.dispatchEvent(new CustomEvent("biometric-success"));
};

const previousOnBiometricFail = window.onBiometricFail;
window.onBiometricFail = (reason?: string) => {
    previousOnBiometricFail?.(reason);
    window.dispatchEvent(new CustomEvent("biometric-fail", { detail: { reason } }));
};

const pathname = window.location.pathname;
const isBOQPopout = pathname === "/popout/boq-editor";
const isUnknownPopout = pathname.startsWith("/popout/") && !isBOQPopout;
const isPublicApproval = pathname.startsWith("/approval/");
const isPublicRamsReview = pathname.startsWith("/rams-review/");
const isPublicWorkerVerify = pathname.startsWith("/verify/");
const isPublicSiteSignIn = pathname.startsWith("/site/");

const rootElement = document.getElementById("root")!;
const root = createRoot(rootElement);

if (isPublicApproval) {
  // Public site-manager landing for Quantis Field K-5 daywork approval.
  // Reached via /approval/:token share links from the operative's phone.
  // Lazy-loaded so the main app bundle stays out of this very small page.
  import("./features/daywork-approval/PublicApprovalPage").then((m) => {
    if (m == null) return; // stale chunk — errorReporter's guarded reload is in flight
    root.render(<m.default />);
  });
} else if (isPublicRamsReview) {
  // Public Employer review landing for a submitted RAMS. Reached via
  // /rams-review/:token links emailed from hs_rams_routes.tsx's transition
  // handler. Same no-app-shell, lazy-loaded shape as the daywork branch above.
  import("./features/rams-review/PublicRAMSReviewPage").then((m) => {
    if (m == null) return; // stale chunk — errorReporter's guarded reload is in flight
    root.render(<m.default />);
  });
} else if (isPublicWorkerVerify) {
  // Public site-manager landing for a scanned worker ID QR code. Reached via
  // /verify/:token links generated by worker_verification_routes.tsx and
  // shown by WorkerVerificationQRDialog.tsx. Same no-app-shell, lazy-loaded
  // shape as the RAMS review branch above.
  import("./features/worker-verification/PublicWorkerVerifyPage").then((m) => {
    if (m == null) return; // stale chunk — errorReporter's guarded reload is in flight
    root.render(<m.default />);
  });
} else if (isPublicSiteSignIn) {
  // Public visitor book for a project's site, reached by scanning the
  // printed site QR with a phone camera at /site/:projectId?k=<siteKey>.
  // Quantis Field intercepts the same universal link for operatives, so this
  // page only ever runs for visitors without the app. Same no-app-shell,
  // lazy-loaded shape as the branches above.
  import("./features/site-access/PublicSiteSignInPage").then((m) => {
    if (m == null) return; // stale chunk — errorReporter's guarded reload is in flight
    root.render(<m.default />);
  });
} else if (isBOQPopout) {
  // Dynamic-import the popout entry so its module graph (PopoutFrame,
  // BOQEditorPopoutEditor, a second AppProviders stack, all of the BOQ
  // editor + its vendor deps) stays out of the main app's entry chunk.
  // Only the /popout/boq-editor window pays for it.
  import("./features/boq/popout/BOQEditorPopoutRoute").then((m) => {
    if (m == null) return; // stale chunk — errorReporter's guarded reload is in flight
    root.render(<m.BOQEditorPopoutRoute />);
  });
} else if (isUnknownPopout) {
  root.render(
    <div style={{ padding: 24, fontFamily: "system-ui" }}>
      <h1>Unknown pop-out</h1>
      <p>This pop-out route is not registered. Close this window.</p>
    </div>,
  );
} else {
  root.render(
    <>
      <WindowTitleBar />
      <App />
    </>,
  );
}
